/**
 * Aufgaben mock data (VITE_DATA_MODE=mock — the queue explorable without a
 * backend). FRONTEND-ONLY fallback (mock layer NOT deleted). Rows are the
 * TaskSummary wire shape verbatim.
 *
 * Due dates are RELATIVE to now (day offsets) so the Überfällig / Diese Woche /
 * Kritisch presets always have members regardless of the run date — the mock
 * stays deterministic for tests. assigned_to uses the administration MOCK_USER
 * ids so the "Meine" preset resolves against the seeded broker session
 * (lara.meier@… → MOCK_USER_A). assignee_name / assigned_group_name mirror the
 * names the api now resolves per row (issue 20), so the Quelle line and the peek
 * read the same in mock and live.
 *
 * [Source: apps/web/src/features/invoices/mock.ts (mock precedent)]
 * [Source: apps/web/src/features/administration/mock.ts (MOCK_USER_A/B)]
 */

import type { TaskRow } from './types';
import type { SplitViewResponse } from '@brokly/shared';
import { MOCK_USER_A, MOCK_USER_B } from '../administration/mock';

const now = Date.now();
/**
 * ISO for `n` calendar days from now (negative = past → overdue), as daysUntil counts them:
 * n * 24 h lands a day off across a DST change.
 * Exported so the Posteingang's Eingang-task fixture shares ONE clock with this one.
 */
export const inDays = (n: number): string => {
  const d = new Date(now);
  d.setDate(d.getDate() + n);
  return d.toISOString();
};

export const AUFGABEN_MOCK: TaskRow[] = [
  {
    id: 'task-inv-k3-01',
    process: 'invoice_k3_escalation',
    status: 'awaiting_approval',
    routing: 'freigabe',
    title: 'Rechnung RE-2026-04812 eskaliert (K3)',
    comm_id: null,
    assigned_to: MOCK_USER_A,
    assigned_group_id: null,
    assignee_name: 'Lara Meier',
    assigned_group_name: null,
    due_at: inDays(-3),
    completed_at: null,
    needs_review: true,
    subject_type: 'invoice',
    subject_id: 'RE-2026-04812',
    confidence: '0.410',
    kunde_name: 'Bergland Logistik AG',
    subject: 'Prämienrechnung Q3',
    from: 'billing@axa.ch',
    received_at: inDays(-4),
    created_at: inDays(-4),
    updated_at: inDays(-1),
  },
  {
    id: 'task-inv-match-02',
    process: 'invoice_manual_match',
    status: 'open',
    routing: 'manual',
    title: 'Rechnung RE-2026-04720 zuordnen',
    comm_id: null,
    assigned_to: null,
    assigned_group_id: 'grp-buchhaltung',
    assignee_name: null,
    assigned_group_name: 'Buchhaltung',
    due_at: inDays(2),
    completed_at: null,
    needs_review: false,
    subject_type: 'invoice',
    subject_id: 'RE-2026-04720',
    confidence: '0.720',
    kunde_name: 'Genève Maritime SA',
    subject: 'Rechnung ohne Police',
    from: 'noreply@swisslife.ch',
    received_at: inDays(-1),
    created_at: inDays(-1),
    updated_at: inDays(-1),
  },
  {
    id: 'task-renewal-03',
    process: 'renewal_due',
    status: 'open',
    routing: 'manual',
    title: 'Renewal Police POL-118-7740 fällig',
    comm_id: null,
    assigned_to: MOCK_USER_A,
    assigned_group_id: null,
    assignee_name: 'Lara Meier',
    assigned_group_name: null,
    due_at: inDays(5),
    completed_at: null,
    needs_review: false,
    subject_type: 'firma',
    subject_id: 'f-gen',
    confidence: null,
    kunde_name: 'Genève Maritime SA',
    subject: null,
    from: null,
    received_at: null,
    created_at: inDays(-10),
    updated_at: inDays(-2),
  },
  {
    id: 'task-comm-match-04',
    process: 'commission_manual_match',
    status: 'in_progress',
    routing: 'manual',
    title: 'Courtageposition zuordnen (August)',
    comm_id: null,
    assigned_to: MOCK_USER_B,
    assigned_group_id: null,
    assignee_name: 'Tomas Bianchi',
    assigned_group_name: null,
    due_at: inDays(1),
    completed_at: null,
    needs_review: false,
    subject_type: 'firma',
    subject_id: 'f-berg',
    confidence: '0.550',
    kunde_name: 'Bergland Logistik AG',
    subject: null,
    from: null,
    received_at: null,
    created_at: inDays(-3),
    updated_at: inDays(0),
  },
  {
    id: 'task-mandate-05',
    process: 'mandate_signing',
    status: 'open',
    routing: 'manual',
    title: 'Maklermandat unterzeichnen lassen',
    comm_id: null,
    assigned_to: MOCK_USER_A,
    assigned_group_id: null,
    assignee_name: 'Lara Meier',
    assigned_group_name: null,
    due_at: inDays(9),
    completed_at: null,
    needs_review: false,
    subject_type: 'person',
    subject_id: 'p-huber',
    confidence: null,
    kunde_name: 'Andrea Huber',
    subject: null,
    from: null,
    received_at: null,
    created_at: inDays(-5),
    updated_at: inDays(-5),
  },
  {
    id: 'task-email-06',
    process: 'email_intake',
    status: 'open',
    routing: 'freigabe',
    title: 'Kundenanfrage: Deckungsänderung',
    comm_id: 'comm-abc',
    assigned_to: null,
    assigned_group_id: null,
    assignee_name: null,
    assigned_group_name: null,
    due_at: inDays(0),
    completed_at: null,
    needs_review: true,
    subject_type: 'person',
    subject_id: 'p-huber',
    confidence: '0.680',
    kunde_name: 'Andrea Huber',
    subject: 'Frage zu meiner Hausratversicherung',
    from: 'andrea.huber@example.ch',
    received_at: inDays(0),
    created_at: inDays(0),
    updated_at: inDays(0),
  },
  {
    id: 'task-portal-07',
    process: 'portal_message',
    status: 'open',
    routing: 'manual',
    title: 'Portalnachricht beantworten',
    comm_id: 'comm-def',
    assigned_to: MOCK_USER_A,
    assigned_group_id: null,
    assignee_name: 'Lara Meier',
    assigned_group_name: null,
    due_at: inDays(4),
    completed_at: null,
    needs_review: false,
    subject_type: 'firma',
    subject_id: 'f-berg',
    confidence: null,
    kunde_name: 'Bergland Logistik AG',
    subject: 'Adressänderung',
    from: 'portal',
    received_at: inDays(-1),
    created_at: inDays(-1),
    updated_at: inDays(-1),
  },
  {
    id: 'task-endorse-08',
    process: 'policy_endorsement_review',
    status: 'awaiting_approval',
    routing: 'freigabe',
    title: 'Nachtrag prüfen: Police POL-883-4471',
    comm_id: null,
    assigned_to: MOCK_USER_B,
    assigned_group_id: null,
    assignee_name: 'Tomas Bianchi',
    assigned_group_name: null,
    due_at: inDays(-1),
    completed_at: null,
    needs_review: true,
    subject_type: 'firma',
    subject_id: 'f-berg',
    confidence: '0.900',
    kunde_name: 'Bergland Logistik AG',
    subject: null,
    from: null,
    received_at: null,
    created_at: inDays(-2),
    updated_at: inDays(-1),
  },
  {
    id: 'task-doc-09',
    process: 'doc_classification',
    status: 'done',
    routing: 'automated',
    title: 'Dokument klassifiziert: Offerte',
    comm_id: null,
    assigned_to: MOCK_USER_A,
    assigned_group_id: null,
    assignee_name: 'Lara Meier',
    assigned_group_name: null,
    due_at: null,
    completed_at: inDays(-1),
    needs_review: false,
    subject_type: 'person',
    subject_id: 'p-huber',
    confidence: '0.980',
    kunde_name: 'Andrea Huber',
    subject: null,
    from: null,
    received_at: null,
    created_at: inDays(-2),
    updated_at: inDays(-1),
  },
  {
    id: 'task-other-10',
    process: 'other',
    status: 'open',
    routing: 'manual',
    title: 'Rückruf: offene Frage klären',
    comm_id: null,
    assigned_to: null,
    assigned_group_id: null,
    assignee_name: null,
    assigned_group_name: null,
    due_at: inDays(14),
    completed_at: null,
    needs_review: false,
    subject_type: null,
    subject_id: null,
    confidence: null,
    kunde_name: null,
    subject: null,
    from: null,
    received_at: null,
    created_at: inDays(-1),
    updated_at: inDays(-1),
  },
];

// ── Split-view mock (the Bearbeitungsmaske + the Aufgaben peek) ────────────────

/**
 * A real 677-byte PDF as a data URI, so the Teil-1 inline preview actually RENDERS in
 * mock mode instead of showing "nicht verfuegbar". `useDocumentBlobUrl` hands `mockUrl`
 * straight to the iframe, and `usePdfObjectUrl`'s byte check is a no-op in mock (there is
 * no server Content-Type to disagree with), so a data: URI is the whole fixture - no
 * binary file in the repo, no dev-server route to 404.
 */
export const MOCK_PDF_DATA_URL = 'data:application/pdf;base64,JVBERi0xLjQKMSAwIG9iago8PCAvVHlwZSAvQ2F0YWxvZyAvUGFnZXMgMiAwIFIgPj4KZW5kb2JqCjIgMCBvYmoKPDwgL1R5cGUgL1BhZ2VzIC9LaWRzIFszIDAgUl0gL0NvdW50IDEgPj4KZW5kb2JqCjMgMCBvYmoKPDwgL1R5cGUgL1BhZ2UgL1BhcmVudCAyIDAgUiAvTWVkaWFCb3ggWzAgMCA1OTUgODQyXSAvUmVzb3VyY2VzIDw8IC9Gb250IDw8IC9GMSA0IDAgUiA+PiA+PiAvQ29udGVudHMgNSAwIFIgPj4KZW5kb2JqCjQgMCBvYmoKPDwgL1R5cGUgL0ZvbnQgL1N1YnR5cGUgL1R5cGUxIC9CYXNlRm9udCAvSGVsdmV0aWNhID4+CmVuZG9iago1IDAgb2JqCjw8IC9MZW5ndGggMTMyID4+CnN0cmVhbQpCVCAvRjEgMTYgVGYgNzIgNzYwIFRkIChQb2xpY2UgNDcxMSAtIE1vY2stRG9rdW1lbnQpIFRqIDAgLTI4IFRkIC9GMSAxMSBUZiAoQnJva2x5IEJlYXJiZWl0dW5nc21hc2tlLCBUZWlsIDEgSW5saW5lLVZvcnNjaGF1LikgVGogRVQKZW5kc3RyZWFtCmVuZG9iagp4cmVmCjAgNgowMDAwMDAwMDAwIDY1NTM1IGYgCjAwMDAwMDAwMDkgMDAwMDAgbiAKMDAwMDAwMDA1OCAwMDAwMCBuIAowMDAwMDAwMTE1IDAwMDAwIG4gCjAwMDAwMDAyNDEgMDAwMDAgbiAKMDAwMDAwMDMxMSAwMDAwMCBuIAp0cmFpbGVyCjw8IC9TaXplIDYgL1Jvb3QgMSAwIFIgPj4Kc3RhcnR4cmVmCjQ5NAolJUVPRgo=';

/** This module's own two PDF ids (the queue split view predates the `-pdf` rule). */
const MOCK_PDF_IDS = new Set(['mock-doc-police', 'mock-doc-rechnung']);

/**
 * The preview src for a mock attachment, or null for one that has no preview.
 *
 * A mock PDF is one whose id ENDS IN `-pdf` — a rule rather than a list, so a fixture in
 * another feature (EINGANG_ATTACHMENTS_MOCK) can mint previewable files without this
 * module importing it, which would be a cycle: the Posteingang fixture already imports
 * `inDays` from here.
 */
export function mockDocumentUrl(documentId: string): string | null {
  return documentId.endsWith('-pdf') || MOCK_PDF_IDS.has(documentId) ? MOCK_PDF_DATA_URL : null;
}

/**
 * The GET /tasks/:id split view for a mock task.
 *
 * The communication ECHOES the row's own facts (subject/from/received_at) rather than
 * inventing new ones: `AufgabePeek` prefers the split view over the row, so anything
 * different here would silently change what the peek shows in mock mode.
 */
export function taskSplitViewMock(task: TaskRow): SplitViewResponse {
  return {
    task: { id: task.id } as SplitViewResponse['task'],
    communication: {
      id: task.comm_id ?? `comm-${task.id}`,
      channel: 'email',
      direction: 'inbound',
      subject: task.subject ?? task.title ?? null,
      from: task.from ?? null,
      to: 'eingang@broker.ch',
      cc: null,
      message_id: null,
      received_at: task.received_at ?? task.created_at,
      body:
        'Guten Tag\n\nanbei die Unterlagen zur Police. Bitte prüfen Sie die beiliegende ' +
        'Rechnung und bestätigen Sie uns den Eingang.\n\nFreundliche Grüsse',
      body_html: null,
    },
    attachments: [
      {
        id: 'mock-doc-police',
        filename: 'Police_4711.pdf',
        mime_type: 'application/pdf',
        size_bytes: 2_412_000,
      },
      {
        id: 'mock-doc-rechnung',
        filename: 'Rechnung_Q3.pdf',
        mime_type: 'application/pdf',
        size_bytes: 184_000,
      },
      {
        id: 'mock-doc-notiz',
        filename: 'Interne_Notiz.docx',
        mime_type: 'application/msword',
        size_bytes: 24_000,
      },
    ],
    activity: [
      {
        id: `act-${task.id}-1`,
        actor_kind: 'system',
        actor_id: null,
        type: 'classification',
        content: 'Eingang klassifiziert und Aufgabe erstellt.',
        meta: null,
        created_at: task.created_at,
      },
      {
        id: `act-${task.id}-2`,
        actor_kind: 'user',
        actor_id: MOCK_USER_A,
        type: 'note',
        content: 'Kunde telefonisch erreicht, Unterlagen werden nachgereicht.',
        meta: null,
        created_at: task.updated_at,
      },
    ],
  };
}
